import React, { useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';

import { NATIVE_HEADER_GEOMETRY, applyHeaderMinWidths } from './lib/headerMinWidth';

const DEFAULT_COL_DEF = {
  flex: 1,
  sortable: true,
  filter: true,
  resizable: true
};

const AGMasterDetailTable = ({
  data,
  detailComponent: DetailComponent,
  columns,
  onReorder,
  onToggleMaster,
  onToggleDetail,
  onDelegateRow,
  onRowClick,
  defaultExpandedId = null,
  rowHeight = 50,
  tableHeight = '450px'
}) => {
  const [expandedRowId, setExpandedRowId] = useState(defaultExpandedId);

  const onFirstDataRendered = (params) => {
    if (defaultExpandedId) {
      params.api.forEachNode((node) => {
        if (node.id === defaultExpandedId.toString()) {
          node.setExpanded(true);
        }
      });
    }
  };

  const onRowExpanded = (params) => {
    if (params.node.expanded) {
      setExpandedRowId(params.node.id);
    } else if (expandedRowId === params.node.id) {
      setExpandedRowId(null);
    }
  };

  const onRowDragEnd = (event) => {
    const overNode = event.overNode;
    if (!overNode || !onReorder) return;

    const allNodes = [];
    event.api.forEachNode((node) => allNodes.push(node));

    const newOrder = allNodes.map((node, index) => ({
      ...node.data,
      order: index + 1
    }));

    onReorder(newOrder);
  };

  const onCellClicked = (params) => {
    const colDef = params.column?.getColDef();
    if (colDef?.suppressNavigable) return;

    if (onDelegateRow && params.data) {
      onDelegateRow(params.data, null);
    }
    onRowClick?.(params);
  };

  const sizedColumns = useMemo(
    () => applyHeaderMinWidths(columns, DEFAULT_COL_DEF, NATIVE_HEADER_GEOMETRY),
    [columns]
  );

  return (
    <div className="ag-theme-quartz" style={{ height: tableHeight, width: '100%' }}>
      <AgGridReact
        rowData={data}
        masterDetail={true}
        detailCellRenderer={DetailComponent}
        columnDefs={sizedColumns}
        defaultColDef={DEFAULT_COL_DEF}
        detailRowAutoHeight={true}
        onFirstDataRendered={onFirstDataRendered}
        rowDragManaged={true}
        onRowDragEnd={onReorder ? onRowDragEnd : undefined}
        onRowGroupOpened={onRowExpanded}
        onCellClicked={onCellClicked}
        suppressMoveWhenRowDragging={true}
        context={{ onToggleDetail, onToggleMaster, onDelegateRow }}
        getRowId={(params) => params.data.id || params.data.code}
        animateRows={true}
        rowHeight={rowHeight}
      />
    </div>
  );
};

AGMasterDetailTable.propTypes = {
  data: PropTypes.array.isRequired,
  detailComponent: PropTypes.elementType.isRequired,
  columns: PropTypes.array.isRequired,
  onReorder: PropTypes.func,
  onToggleMaster: PropTypes.func,
  onToggleDetail: PropTypes.func,
  onDelegateRow: PropTypes.func,
  onRowClick: PropTypes.func,
  defaultExpandedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rowHeight: PropTypes.number,
  tableHeight: PropTypes.string
};

export default AGMasterDetailTable;
